import React, { useState, useEffect } from 'react';
import { Star, Flame } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { getMenuItems, getMenuCategories } from '../services/api';

const MenuSection = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState(['Semua']);
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      const cats = await getMenuCategories();
      setCategories(Array.isArray(cats) ? cats : ['Semua']);
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchMenu = async () => {
      setLoading(true);
      const items = await getMenuItems(activeCategory);
      setMenuItems(Array.isArray(items) ? items : []);
      setLoading(false);
    };
    fetchMenu();
  }, [activeCategory]);

  const safeMenuItems = Array.isArray(menuItems) ? menuItems : [];
  const safeCategories = Array.isArray(categories) ? categories : ['Semua'];
  const popularItems = safeMenuItems.filter((item) => item && item.popular);

  return (
    <section id="menu" className="py-24 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
            Menu Kami
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Hidangan Lezat Khas Padang
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Nikmati berbagai pilihan masakan Padang autentik dengan cita rasa yang tak terlupakan
          </p>
        </div>

        {popularItems.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-8">
              <Flame className="w-6 h-6 text-red-500" />
              <h3 className="text-2xl font-bold text-gray-900">Menu Favorit</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularItems.slice(0, 4).map((item) => (
                <Card
                  key={item.id}
                  className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 bg-white"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <Badge className="absolute top-3 right-3 bg-red-500 text-white border-0">
                      <Star className="w-3 h-3 mr-1 fill-white" />
                      Favorit
                    </Badge>
                    <div className="absolute bottom-3 left-3 right-3">
                      <span className="text-amber-400 font-bold text-lg">{item.price}</span>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-bold text-gray-900 text-lg mb-1">{item.name}</h4>
                    <p className="text-gray-600 text-sm line-clamp-2">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          {safeCategories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2.5 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-gradient-to-r from-amber-500 to-red-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-amber-50 border border-gray-200 hover:border-amber-300'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Memuat menu...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {safeMenuItems.map((item) => (
              <Card
                key={item.id}
                className="group overflow-hidden border border-gray-100 hover:border-amber-200 shadow-sm hover:shadow-xl transition-all duration-300 bg-white"
              >
                <div className="flex">
                  <div className="w-32 h-32 flex-shrink-0 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-4 flex flex-col justify-center flex-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-bold text-gray-900">{item.name}</h4>
                      {item.popular && (
                        <Flame className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-gray-500 text-sm mb-2 line-clamp-2">
                      {item.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-amber-600 font-bold">{item.price}</span>
                      <Badge variant="secondary" className="text-xs">
                        {item.category}
                      </Badge>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MenuSection;
